import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useFilterStore, StoreType } from '@/store/filter';
import { STORE_TYPES } from '@/constants';
import { DesktopLayout } from '@/components/desktop/DesktopLayout';

interface FilterOption {
  id: string;
  label: string;
  selected: boolean;
}

const initialStoreTypes: FilterOption[] = [
  { id: 'all', label: '전체 선택', selected: false },
  ...STORE_TYPES.map(type => ({ ...type, selected: false }))
];

export function DesktopViewFilterPage() {
  const navigate = useNavigate();
  const { 
    selectedStoreTypes, 
    showOpenOnly, 
    showClosedOnly, 
    setSelectedStoreTypes, 
    setShowOpenOnly, 
    setShowClosedOnly 
  } = useFilterStore();
  
  const [storeTypes, setStoreTypes] = useState<FilterOption[]>(initialStoreTypes);
  const [additionalFilters, setAdditionalFilters] = useState<FilterOption[]>([
    { id: 'open_now', label: '주문 가능', selected: false },
    { id: 'closed', label: '주문 불가', selected: false },
  ]);

  // Restore saved filter state on mount
  useEffect(() => {
    setStoreTypes(prev => prev.map(type => ({
      ...type,
      selected: type.id === 'all' 
        ? selectedStoreTypes.length === 0 || selectedStoreTypes.length === prev.length - 1
        : selectedStoreTypes.includes(type.id as StoreType)
    })));

    setAdditionalFilters(prev => prev.map(filter => ({
      ...filter,
      selected: filter.id === 'open_now' ? showOpenOnly : filter.id === 'closed' ? showClosedOnly : false
    })));
  }, [selectedStoreTypes, showOpenOnly, showClosedOnly]);

  const handleStoreTypeToggle = (id: string) => {
    if (id === 'all') {
      const newValue = !storeTypes[0].selected;
      setStoreTypes(storeTypes.map(type => ({ ...type, selected: newValue })));
    } else {
      setStoreTypes(storeTypes.map(type => 
        type.id === id ? { ...type, selected: !type.selected } : type
      ));
    }
  };

  const handleAdditionalFilterToggle = (id: string) => {
    setAdditionalFilters(additionalFilters.map(filter => 
      filter.id === id ? { ...filter, selected: !filter.selected } : filter
    ));
  };

  const handleApplyFilter = () => {
    // Save selected store types
    const selectedTypes = storeTypes
      .filter(type => type.selected && type.id !== 'all')
      .map(type => type.id as StoreType);
    
    setSelectedStoreTypes(selectedTypes);
    
    // Save additional filters
    const openFilter = additionalFilters.find(f => f.id === 'open_now');
    const closedFilter = additionalFilters.find(f => f.id === 'closed');
    
    setShowOpenOnly(openFilter?.selected || false);
    setShowClosedOnly(closedFilter?.selected || false);
    
    navigate(-1);
  };

  const handleReset = () => {
    setStoreTypes(initialStoreTypes);
    setAdditionalFilters([
      { id: 'open_now', label: '주문 가능', selected: false },
      { id: 'closed', label: '주문 불가', selected: false },
    ]);
    setSelectedStoreTypes([]);
    setShowOpenOnly(false);
    setShowClosedOnly(false);
  };

  const activeFilterCount = 
    storeTypes.filter(t => t.selected && t.id !== 'all').length + 
    additionalFilters.filter(f => f.selected).length;

  return (
    <DesktopLayout>
      <div className="max-w-4xl mx-auto py-8">
        <div className="bg-white rounded-xl shadow-sm">
          {/* Header */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button 
                  onClick={() => navigate(-1)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                    <path d="M15 18L9 12L15 6" stroke="#222222" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                <div>
                  <h1 className="text-2xl font-bold text-[#222222]">필터 설정</h1>
                  {activeFilterCount > 0 && (
                    <p className="text-sm text-gray-600 mt-1">
                      {activeFilterCount}개 필터 적용중
                    </p>
                  )}
                </div>
              </div>
              
              <button
                onClick={handleReset}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors"
              >
                초기화
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Store Types Section */}
            <section>
              <h2 className="text-lg font-semibold text-[#222222] mb-4">매장종류</h2>
              <div className="grid grid-cols-4 gap-3">
                {storeTypes.map((type) => (
                  <button
                    key={type.id}
                    onClick={() => handleStoreTypeToggle(type.id)}
                    className={cn(
                      "px-6 py-3 rounded-xl text-base font-medium transition-all",
                      type.selected
                        ? "bg-[#86DB89] text-white shadow-md transform scale-105"
                        : "bg-[#F5F5F5] text-[#666666] hover:bg-gray-200"
                    )}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            </section>

            {/* Divider */}
            <div className="my-8 border-t border-gray-200"></div>

            {/* Additional Filters Section */}
            <section>
              <h2 className="text-lg font-semibold text-[#222222] mb-4">추가 필터</h2>
              <div className="grid grid-cols-4 gap-3">
                {additionalFilters.map((filter) => (
                  <button
                    key={filter.id}
                    onClick={() => handleAdditionalFilterToggle(filter.id)}
                    className={cn(
                      "px-6 py-3 rounded-xl text-base font-medium transition-all",
                      filter.selected
                        ? "bg-[#86DB89] text-white shadow-md transform scale-105"
                        : "bg-[#F5F5F5] text-[#666666] hover:bg-gray-200"
                    )}
                  >
                    {filter.label}
                  </button>
                ))}
              </div>
            </section>

            {/* Selected Filters Summary */}
            {activeFilterCount > 0 && (
              <div className="mt-8 p-4 bg-green-50 rounded-xl">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">선택된 필터:</h3>
                <div className="flex flex-wrap gap-2">
                  {storeTypes
                    .filter(t => t.selected && t.id !== 'all')
                    .map(type => (
                      <span 
                        key={type.id}
                        className="px-3 py-1 bg-white text-sm text-gray-700 rounded-full border border-green-300"
                      >
                        {type.label}
                      </span>
                    ))
                  }
                  {additionalFilters
                    .filter(f => f.selected)
                    .map(filter => (
                      <span 
                        key={filter.id}
                        className="px-3 py-1 bg-white text-sm text-gray-700 rounded-full border border-green-300"
                      >
                        {filter.label}
                      </span>
                    ))
                  }
                </div>
              </div>
            )}
          </div>

          {/* Bottom Actions */}
          <div className="p-6 border-t border-gray-200">
            <div className="flex gap-4">
              <button
                onClick={() => navigate(-1)}
                className="flex-1 h-14 bg-gray-200 rounded-xl text-lg font-semibold text-gray-700 hover:bg-gray-300 transition-colors"
              >
                취소
              </button>
              <button
                onClick={handleApplyFilter}
                className="flex-1 h-14 bg-green-500 rounded-xl text-lg font-semibold text-white hover:bg-green-600 transition-colors"
              >
                필터 적용
              </button>
            </div>
          </div>
        </div>
      </div>
    </DesktopLayout>
  );
}